import { SAME_ADDRESS_SKIPPED_KEYS } from '../shared/leadGenAnswers'

const isYes = (value) => String(value) === 'true'

export const hasCoborrower = (state) => isYes(state.answers?.has_coborrower)

const coborrowerSharesAddress = (state) => isYes(state.answers?.coborrower_same_address)

export default {
  getHasCoborrower: hasCoborrower,
  getLeadGenQuestions: (state) => {
    const questions = state.leadGenQuestions || []

    if (state.previewQuestion) {
      return questions
    }

    if (!hasCoborrower(state)) {
      return questions.filter(question => !question.for_coborrower)
    }

    if (coborrowerSharesAddress(state)) {
      return questions.filter(question => !SAME_ADDRESS_SKIPPED_KEYS.includes(question.key))
    }

    return questions
  },
  getAnswers: (state) => {
    return state.answers
  },
  isPrequalFlowEnabled: (state) => {
    return state.settings.prequalFlow
  },
  isCreditEnabled: (state) => {
    if (state.conversationalCreditEnabled !== null && state.conversationalCreditEnabled !== undefined) {
      return state.conversationalCreditEnabled
    }
    const settings = state.settings || {}
    return !!(
      settings.autoRunCredit &&
      settings.hasCreditIntegration &&
      settings.prequalCreditEligible !== false &&
      settings.shortFormCreditEligible !== false
    )
  },
  isPrequalLoanType: (state) => {
    return state.answers.loan_type === 'prequal'
  },
  getTotalQuestions: (state, getters) => {
    const currentQuestions = getters.getLeadGenQuestions?.length || 0
    const prequalCount = state.prequalQuestionCount || 0
    if (state.answers?.loan_type === 'prequal' && state.leadGenQuestionCount > 0) {
      return state.leadGenQuestionCount + (currentQuestions || prequalCount)
    }
    if (state.settings?.prequalFlow && state.answers?.loan_type !== 'prequal' && prequalCount > 0) {
      return currentQuestions + prequalCount
    }
    return currentQuestions
  },
  getLeadGenQuestionCount: (state) => {
    return state.leadGenQuestionCount
  },
  getProgressPercentage: (state, getters) => {
    const activeLen = getters.getLeadGenQuestions?.length || 0
    const shortForm = state.leadGenQuestionCount || 0
    const total = activeLen > 0 ? getters.getTotalQuestions : (shortForm + (state.prequalQuestionCount || 0))
    if (!total) {
      return 0
    }
    const isPrequalPhase = !state.isInConversationalFlow && state.answers?.loan_type === 'prequal'
    const offset = (isPrequalPhase && shortForm > 0) ? shortForm : 0
    const position = offset + state.currentQuestionIndex + 1
    if (position <= 0) {
      return 0
    }
    return Math.min((position / (total + 1)) * 100, 100)
  },
  getOrgGuid: (state) => {
    return state.orgGuid
  },
  getOrgId: (state) => {
    return state.orgId
  },
  getOrgType: (state) => {
    return state.orgType
  },
  getSettings: (state) => {
    return state.settings
  },
  getOrgIcon: (state) => {
    return state.settings.orgIcon
  },
  getOrgAppIcon: (state) => {
    return state.settings.orgAppIcon
  },
  getOrgName: (state) => {
    return state.settings.name
  },
  getServicerImageUrl: (state) => {
    return state.settings.servicerImageUrl
  },
  getServicerInitials: (state) => {
    return state.settings.servicerInitials
  },
  getPartnerImageUrl: (state) => {
    return state.settings.partnerImageUrl
  },
  getPartnerInitials: (state) => {
    return state.settings.partnerInitials
  },
  getLandingPageTitle: (state) => {
    return state.settings.landingPageTitle
  },
  getSubmissionPageBodyText: (state) => {
    return state.settings.submissionPageBodyText
  },
  getAnswer: (state) => (questionKey) => {
    return state.answers[questionKey]
  },
  getCurrentQuestionIndex: (state) => {
    return state.currentQuestionIndex
  },
  isInLeadGenForm: (state) => {
    return state.leadGenQuestions && state.leadGenQuestions.length > 0 && !state.formSubmitted
  },
  isFormSubmitted: (state) => {
    return state.formSubmitted
  },
  getSac: (state) => {
    return state.sac
  },
  getLeadId: (state) => {
    return state.leadId
  },
  getSelectedLoanType: (state) => {
    return state.selectedLoanType
  },
  getPreviewForm: (state) => {
    return state.previewForm
  },
  getPreviewQuestion: (state) => {
    return state.previewQuestion
  },
  getShowLandingPage: (state) => {
    return state.showLandingPage
  },
  showBackButton: (state) => {
    return (state.hasVisitedLandingPage && state.answers?.loan_type !== 'prequal') || state.currentQuestionIndex > 0 || (state.settings?.conversationalFlow && state.answers?.loan_type !== 'prequal')
  },
  isInConversationalFlow: (state) => {
    return state.isInConversationalFlow
  },
  getConversationalChatId: (state) => {
    return state.conversationalChatId
  },
  getConversationalLeadData: (state) => {
    return state.conversationalLeadData
  },
  isConversationalContactCollected: (state) => {
    return state.conversationalContactCollected
  },
  isConversationalLeadComplete: (state) => {
    return state.conversationalLeadComplete
  },
  isConversationalPrequalComplete: (state) => {
    return state.conversationalPrequalComplete
  },
  isConversationalCreditReady: (state) => {
    return state.conversationalCreditReady
  },
  getConversationalMessages: (state) => {
    return state.conversationalMessages
  },
  getConversationalQuestionKeys: (state) => {
    return state.conversationalQuestionKeys
  },
  getConversationalCurrentQuestionKey: (state) => {
    return state.conversationalCurrentQuestionKey
  },
  getConversationalQuickReplies: (state) => {
    return state.conversationalQuickReplies
  },
  getConversationalQuickRepliesAreSuggestions: (state) => {
    return state.conversationalQuickRepliesAreSuggestions
  },
  isConversationalConsentSubmitted: (state) => {
    return state.conversationalConsentSubmitted
  },
  getCompletionPageType: (state) => {
    return state.completionPageType
  },
  getHasAccount: (state) => {
    return state.hasAccount
  },
  getLetterGuid: (state) => {
    return state.letterGuid
  },
  getArtifactGuid: (state) => {
    return state.artifactGuid
  },
  getServicer: (state) => {
    return state.servicer
  },
  getPartner: (state) => {
    return state.partner
  }
}
